import { useEffect, useState, useMemo, useReducer } from 'react';
import { userAppContext } from '../../../../../Context/Context.jsx';

import TableColumnsMenu from '../menu/TableColumnsMenu';
import { EVIDENCE_LEGEND } from '../../constants';

import RenderInfoLogic from '../../../../../Components/InfoSidebar/RenderInfoLogic.jsx';
import Grid from '../../../../../Components/Table/Grid.jsx';
import { Columns } from '../../../../../Components/Table/Columns.jsx';

import RenderSourcesLogic from '../source/RenderSourcesLogic';

import SharedButton from '../../../../../global/Button/Button';
import { AdjustmentsHorizontalIcon } from '@heroicons/react/16/solid';
import { ArrowDownTrayIcon } from '@heroicons/react/16/solid';

import { initialState, searchComponentReducer } from '../../../reducers/searchComponentReducer';
import { DOWNLOAD_EXTENSIONS } from '../../constants';
import { fetchDownloadFile } from '../../apiCalls';

import { useColumnVisibility } from '../../../../../hooks/search/useColumnVisibility.ts';
import { ColumnVisibility, ExtensionFile, UserContext } from '../../../../../types';
import SharedDialog from '../../../../../global/Modal/Modal';
import Alert from '../../../../../global/Alert/Alert.tsx';

function ManageGrid(props): JSX.Element | null {
  const {
    isMapping,
    mappingContent,
    restrictChemical,
    view,
    tab,
    isEvidence,
    source,
    searchItems,
    setSearchItems,
    isInfoSidebarOpen,
    setIsInfoSidebarOpen,
  } = props;

  const { columnVisibilityModel, setColumnVisibilityModel } = useColumnVisibility({
    isMapping,
    mappingContent,
    restrictChemical,
    view,
    tab,
    isEvidence,
  });

  const { authorization, isAuthenticated, selectedInfoItem } = userAppContext() as unknown as UserContext;
  const [totalResults, setTotalResults] = useState(0);
  const [isColumnSelectOpen, setIsColumnSelectOpen] = useState(false);
  const [sourceOpen, setSourceOpen] = useState(false);
  const [_state, dispatch] = useReducer(searchComponentReducer, initialState);
  const [showDownloadConfirm, setShowDownloadConfirm] = useState(false);
  const [pendingDownloadExt, setPendingDownloadExt] = useState<ExtensionFile | null>(null);
  const [showAlert, setShowAlert] = useState(false);
  const [alertType, setAlertType] = useState<'info' | 'success' | 'error' | 'warning'>('success');

  const alertMessage = () => {
    if(alertType === 'success') {
      return 'Successfully downloaded!';
    }
    if(alertType === 'error') {
      return 'Error downloading file.';
    }
    if(alertType === 'warning') {
      return 'Download is being processed.';
    }
  };

  const performDownload = async (extension: ExtensionFile) => {
    try {
      setAlertType('warning');
      setShowAlert(true);
      dispatch({ type: 'SET_LOADING', payload: true });

      await fetchDownloadFile({
        extension: extension.value,
        columnVisibilityModel: columnVisibilityModel as ColumnVisibility,
        view,
        source,
        tab,
        searchItems,
        titleGrid,
        isAuthenticated,
        authorization,
        isEvidence,
        isMapping,
        mappingContent,
      });
    } catch (error) {
      setAlertType('error');
      setShowAlert(true);
      console.error('Error downloading file:', error);
    } finally {
      setAlertType('success');
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  };

  const handleDownloadFiles = async (extension: ExtensionFile) => {
    setPendingDownloadExt(extension);
    if (totalResults > 55000) {
      setShowDownloadConfirm(true);
      setPendingDownloadExt(extension);
      return;
    }

    await performDownload(extension);
  };

  const titleGrid = isEvidence ? 'Evidence' : 'Summary';

  // Memoized columns based on columnVisibilityModel
  const columns = useMemo(() => {
    return columnVisibilityModel
      ? Object.keys(columnVisibilityModel).flatMap(key =>
          Columns.filter(column => key === column.name && columnVisibilityModel[column.name] === true),
        )
      : null;
  }, [columnVisibilityModel]);

  useEffect(() => {
    const searchAndMappingItems = searchItems ? searchItems.concat(mappingContent?.mappingItems) : [];
    if (isInfoSidebarOpen && searchItems && !searchItems.find(item => item.id === selectedInfoItem.id)) {
      if (!searchAndMappingItems.find(item => item.id === selectedInfoItem.id)) {
        setIsInfoSidebarOpen(false);
      }
    }
  }, [searchItems, selectedInfoItem, mappingContent?.mappingItems, isInfoSidebarOpen, setIsInfoSidebarOpen]);

  return !columns ? (
    null
  ) : (
    <>
      {showAlert && (
        <div
          style={{
            position: 'fixed',
            bottom: '1.5rem',
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 9999,
          }}
        >
          <Alert message={alertMessage() as string} type={alertType} onClose={() => setShowAlert(false)} />
        </div>
      )}

      {showDownloadConfirm && (
        <SharedDialog
          isOpen={showDownloadConfirm}
          onClose={() => setShowDownloadConfirm(false)}
          title="Large file download warning"
          description="The file you are requesting contains more than 55,000 records. Downloads are limited to this number. We suggest using the REST API for this request."
          confirmText="Yes"
          cancelText="No"
          onConfirm={async () => {
            if (pendingDownloadExt) {
              await performDownload(pendingDownloadExt);
              setPendingDownloadExt(null);
            }
            setShowDownloadConfirm(false);
          }}
          data-testid="modal"
        >
          <p className="font-medium text-gray-900 mt-2">Do you want to proceed?</p>
        </SharedDialog>
      )}
      <div className="Cabtabla bg-white mt-3">
        {searchItems && (
          <>
            <div className="row">
              <RenderInfoLogic
                searchItems={searchItems}
                selectedInfoItem={selectedInfoItem}
                setSearchItems={setSearchItems}
                isMapping={isMapping}
              />
              <div className="col-4 text-right">
                <div className="row justify-content-end items-center">
                  <SharedButton
                    onClick={() => setIsColumnSelectOpen(!isColumnSelectOpen)}
                    arrow={isColumnSelectOpen ? 'up' : 'down'}
                    className="mt-2"
                  >
                    <AdjustmentsHorizontalIcon className="w-5 mr-2" />
                    Select Columns
                  </SharedButton>
                  <SharedButton
                    onClick={() => setSourceOpen(!sourceOpen)}
                    arrow={sourceOpen ? 'up' : 'down'}
                    className="mt-2"
                  >
                    Source: {source.replace('_', ' ')}
                  </SharedButton>
                </div>
                <div className="justify-content-end mt-2">
                  {view !== 'ALL' &&
                    DOWNLOAD_EXTENSIONS.map((extension, index) => (
                      <SharedButton key={index} onClick={() => handleDownloadFiles(extension)}>
                        {extension.label}
                        <ArrowDownTrayIcon className="w-4 ml-2" />
                      </SharedButton>
                    ))}
                </div>
              </div>
              <div>{sourceOpen && <RenderSourcesLogic />}</div>
            </div>
            {isColumnSelectOpen && (
              <TableColumnsMenu
                view={view}
                tab={tab}
                setIsColumnSelectOpen={setIsColumnSelectOpen}
                columnVisibilityModel={columnVisibilityModel}
                setColumnVisibilityModel={setColumnVisibilityModel}
              />
            )}
            <div className="titEtiqueta">
              <span>{titleGrid}</span>
            </div>
          </>
        )}
      </div>

      {isEvidence && EVIDENCE_LEGEND}

      <Grid
        columns={columns}
        columnVisibilityModel={columnVisibilityModel}
        view={view}
        source={source}
        tab={tab}
        searchItems={searchItems}
        isEvidence={isEvidence}
        isMapping={isMapping}
        setTotalResults={setTotalResults}
      />
    </>
  );
}

export default ManageGrid;
